import { ctx, W, H } from '../canvas.js';
import { state } from '../state.js';
import { t } from '../i18n.js';
import {
  UI_FONT,
  WASTED_FADE_MS,
  WASTED_FONT_PX,
  WASTED_MS,
  WASTED_TRACKING,
  WASTED_WASH,
} from '../config.js';

/**
 * The caption after a kill.
 *
 * The scene goes dark red, the lettering lands in the middle of it, and a tip
 * nobody asked for sits underneath — which is the whole shape of the screen this
 * is tipping its hat to.
 *
 * Drawn in screen space, outside the shake transform: a caption that shakes
 * leaves the edges of its own wash showing, and a wash that does not reach the
 * edges is not a wash, it is a rectangle.
 */
export function drawWasted() {
  if (!wastedActive()) return;

  const age = state.clock - state.wasted.at;

  // In fast, out slow. A caption that fades in as slowly as it fades out reads
  // as broken rather than as deliberate.
  const alpha = age < WASTED_FADE_MS
    ? age / WASTED_FADE_MS
    : age > WASTED_MS - WASTED_FADE_MS
      ? (WASTED_MS - age) / WASTED_FADE_MS
      : 1;

  const cx = W / 2;
  const cy = H / 2 - 20;

  ctx.save();

  ctx.globalAlpha = alpha * WASTED_WASH;
  ctx.fillStyle = '#1a0507';
  ctx.fillRect(0, 0, W, H);

  ctx.globalAlpha = alpha;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.font = `900 ${WASTED_FONT_PX}px ${UI_FONT}`;
  drawTracked('WASTED', cx, cy, WASTED_TRACKING);

  ctx.font = `700 12px ${UI_FONT}`;
  ctx.fillStyle = '#c9a3a8';
  ctx.fillText(t('wasted.subtitle'), cx, cy + WASTED_FONT_PX / 2 + 36);

  ctx.restore();
}

/** True while the caption is up. Also what the render order asks before drawing. */
export function wastedActive() {
  const w = state.wasted;
  if (!w) return false;

  const age = state.clock - w.at;
  return age >= 0 && age <= WASTED_MS;
}

/**
 * Lettering laid out at an advance of our own rather than the font's.
 *
 * `ctx.letterSpacing` would be one line, but it is recent enough that on anything
 * older the caption comes out cramped — and a cramped version of this particular
 * word loses the joke. Each letter is stroked and then filled so it keeps a dark
 * edge over the fireball behind it.
 */
export function drawTracked(text, cx, cy, tracking, fill = '#f7f2ea') {
  const letters = [...text];
  const widths = letters.map(ch => ctx.measureText(ch).width);
  const total = widths.reduce((sum, w) => sum + w, 0) + tracking * (letters.length - 1);

  let x = cx - total / 2;

  letters.forEach((ch, i) => {
    const mid = x + widths[i] / 2;

    ctx.lineWidth = 7;
    ctx.strokeStyle = '#12060a';
    ctx.strokeText(ch, mid, cy);

    // The fill is the caller's, and has to be: the caption is not always the same
    // colour. It was hardcoded here for a long time, which quietly beat whatever the
    // caller had set on the context — a caller that set `fillStyle` and then called
    // this was writing a colour nothing would ever read. The dark outline above is
    // not the caller's: it is what keeps any of these captions legible over a city.
    ctx.fillStyle = fill;
    ctx.fillText(ch, mid, cy);

    x += widths[i] + tracking;
  });
}
